import React, { useState } from 'react';
import { Field } from './Modal';
import { presetsFor, savePreset } from '../utils/propertyPresets';

/**
 * <PropertyFields /> — clean, config-driven editable form for the contractor-
 * filled property tabs. Yes/No fields are one-tap toggles; pick/multi use preset
 * chips with add-custom.
 *
 * Suggested defaults are NOT pre-filled as answers: a field with `suggest` shows
 * "Suggested: X" and a "Use suggestion" button, so nothing is saved as a fact
 * until someone confirms it.
 *
 * spec: [{ key, label, type:'text'|'date'|'yesno'|'pick'|'multi', options?, presetKey?, suggest?, placeholder? }]
 * values: { [key]: value }   onChange(key, value)
 */
const chipStyle = (on) => ({
  fontSize: 'var(--cs-text-sm)', fontWeight: 600, borderRadius: 999, padding: '6px 12px', cursor: 'pointer', minHeight: 36,
  border: `1px solid ${on ? 'var(--cw-blue)' : 'var(--cw-border)'}`,
  background: on ? 'var(--cw-blue)' : 'var(--cw-surface)',
  color: on ? 'var(--cs-white, #fff)' : 'var(--cw-text-2)',
});

const Toggle = ({ value, onChange, id }) => (
  <div style={{ display: 'flex', gap: 8 }} id={id}>
    {['Yes', 'No'].map((o) => (
      <button key={o} type="button" onClick={() => onChange(value === o ? '' : o)} aria-pressed={value === o}
        className={`cw-btn ${value === o ? 'cw-btn-primary' : 'cw-btn-secondary'}`}
        style={{ flex: 1, justifyContent: 'center' }}>
        {value === o && <i className="fa-solid fa-check" aria-hidden="true" />} {o}
      </button>
    ))}
  </div>
);

const Pick = ({ options, value, onChange, presetKey, label, id }) => {
  const [opts, setOpts] = useState(options);
  const known = opts.includes(value);
  return (
    <div id={id}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {opts.map((o) => (
          <button key={o} type="button" aria-pressed={value === o} onClick={() => onChange(value === o ? '' : o)} style={chipStyle(value === o)}>
            {value === o && <i className="fa-solid fa-check" style={{ marginRight: 5 }} aria-hidden="true" />}{o}
          </button>
        ))}
      </div>
      <input className="cw-input" style={{ marginTop: 6 }} value={known ? '' : (value || '')}
        aria-label={`${label} — another answer`} placeholder="…or type another" onChange={(e) => onChange(e.target.value)}
        onBlur={() => { if (value && !opts.includes(value) && presetKey) { setOpts(savePreset(presetKey, value)); } }} />
    </div>
  );
};

const Multi = ({ presetKey, value = [], onChange, label, id }) => {
  const [opts, setOpts] = useState(() => presetsFor(presetKey));
  const [custom, setCustom] = useState('');
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  const addCustom = () => { const v = custom.trim(); if (!v) return; setOpts(savePreset(presetKey, v)); if (!value.includes(v)) onChange([...value, v]); setCustom(''); };
  return (
    <div id={id}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {opts.map((o) => (
          <button key={o} type="button" aria-pressed={value.includes(o)} onClick={() => toggle(o)} style={chipStyle(value.includes(o))}>
            {value.includes(o) && <i className="fa-solid fa-check" style={{ marginRight: 5 }} aria-hidden="true" />}{o}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <input className="cw-input" style={{ flex: 1 }} value={custom} placeholder="Add another…" aria-label={`Add another ${String(label).toLowerCase()}`}
          onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} />
        <button type="button" className="cw-btn cw-btn-secondary" onClick={addCustom}>Add</button>
      </div>
    </div>
  );
};

const isEmpty = (v) => v == null || v === '' || (Array.isArray(v) && !v.length);

const PropertyFields = ({ spec, values, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 16 }} className="cw-field-grid">
    {spec.map((f) => {
      const full = f.type === 'multi' || f.full;
      const val = values[f.key];
      const suggestion = f.suggest != null && isEmpty(val) ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>Suggested: {Array.isArray(f.suggest) ? f.suggest.join(', ') : f.suggest} — not confirmed</span>
          <button type="button" className="cw-btn-link" onClick={() => onChange(f.key, f.suggest)}>Use suggestion</button>
        </span>
      ) : null;
      let control = null;
      if (f.type === 'yesno') control = <Toggle value={val} onChange={(v) => onChange(f.key, v)} />;
      else if (f.type === 'text') control = <input value={val || ''} placeholder={f.placeholder || ''} onChange={(e) => onChange(f.key, e.target.value)} />;
      else if (f.type === 'date') control = <input type="date" value={(val || '').slice(0, 10)} onChange={(e) => onChange(f.key, e.target.value)} />;
      else if (f.type === 'pick') control = <Pick options={f.options || presetsFor(f.presetKey)} presetKey={f.presetKey} label={f.label} value={val || ''} onChange={(v) => onChange(f.key, v)} />;
      else if (f.type === 'multi') control = <Multi presetKey={f.presetKey} label={f.label} value={Array.isArray(val) ? val : []} onChange={(v) => onChange(f.key, v)} />;
      return (
        <Field key={f.key} label={f.label} hint={suggestion} style={full ? { gridColumn: '1 / -1' } : undefined}>
          {control}
        </Field>
      );
    })}
  </div>
);

export default PropertyFields;
